import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Cookies = () => {
  const { t, i18n } = useTranslation();

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('cookies.title')} | Tap'nGrab</title>
        <meta name="description" content="Cookie notice for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${t('cookies.title')} | Tap'nGrab`} />
        <meta property="og:description" content="Cookie notice for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta property="og:url" content={`https://tapngrab.ca/${i18n.language === 'fr' ? 'fr/' : ''}cookies`} />
        <meta property="og:image" content="https://tapngrab.ca/images/smart-store.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={i18n.language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${t('cookies.title')} | Tap'nGrab`} />
        <meta name="twitter:description" content="Cookie notice for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta name="twitter:image" content="https://tapngrab.ca/images/smart-store.png" />
        <link rel="canonical" href="https://tapngrab.ca/cookies" />
        <link rel="alternate" hreflang="fr" href="https://tapngrab.ca/fr/cookies" />
        <link rel="alternate" hreflang="en" href="https://tapngrab.ca/cookies" />
      </Helmet>

      <div className="text-slate-800 bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="hero-grad py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-slate-900 sm:text-5xl lg:text-6xl">
                {t('cookies.title')}
              </h1>
              <p className="mt-6 text-lg text-slate-600">
                {t('cookies.last_updated')}
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              <div className="bg-slate-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  {t('cookies.what_are_cookies')}
                </h2>
                <p className="text-lg text-slate-700 leading-relaxed">
                  {t('cookies.cookies_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('cookies.types_cookies')}
                </h2>
                
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-slate-800">
                    {t('cookies.essential_cookies')}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {t('cookies.essential_description')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {t('cookies.essential_list', { returnObjects: true }).map((item, index) => (
                      <li key={index} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-slate-800">
                    {t('cookies.analytics_cookies')}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {t('cookies.analytics_description')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {t('cookies.analytics_list', { returnObjects: true }).map((item, index) => (
                      <li key={index} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-slate-800">
                    {t('cookies.functional_cookies')}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {t('cookies.functional_description')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {t('cookies.functional_list', { returnObjects: true }).map((item, index) => (
                      <li key={index} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('cookies.how_we_use_cookies')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('cookies.use_cookies_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('cookies.use_cookies_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('cookies.third_party_cookies')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('cookies.third_party_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('cookies.third_party_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('cookies.managing_preferences')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('cookies.managing_description')}
                </p>
                
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-slate-800">
                    {t('cookies.browser_settings')}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {t('cookies.browser_description')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {t('cookies.browser_list', { returnObjects: true }).map((item, index) => (
                      <li key={index} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-slate-800">
                    {t('cookies.browser_instructions')}
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {t('cookies.browser_instructions_list', { returnObjects: true }).map((item, index) => (
                      <li key={index} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: item }}></li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('cookies.impact_disabling')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('cookies.impact_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('cookies.impact_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('cookies.retention_period')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('cookies.retention_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('cookies.retention_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('cookies.updates_notice')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('cookies.updates_description')}
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-8 space-y-6">
                <h2 className="text-2xl font-bold text-slate-900">
                  {t('cookies.contact_us')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('cookies.contact_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('cookies.contact_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('cookies.your_rights')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('cookies.rights_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('cookies.rights_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Cookies;
